import { ChangeEvent } from "react";

export type PbxExtensionOption = {
  number: string;
  type?: string;
  status?: string;
  mapped?: Array<{ id?: string | number; name?: string }>;
};

function optionLabel(n: string, row?: PbxExtensionOption, currentUserId?: string) {
  const type = String(row?.type || "sip").toUpperCase();
  const status = row?.status ? String(row.status) : "";
  const others = (row?.mapped || []).filter(
    (m) => !currentUserId || String(m.id) !== String(currentUserId),
  );
  const agent = others.map((m) => m.name).filter(Boolean).join(", ");
  const avail = agent ? `taken by ${agent}` : "available";
  const parts = [n, type !== "SIP" ? type : null, status, avail];
  return parts.filter(Boolean).join(" · ");
}

export function ExtensionSelect({
  name,
  defaultValue = "",
  value,
  onChange,
  extensions,
  required,
  allowEmpty = true,
  emptyLabel = "Not mapped",
  loading = false,
  disabled,
  currentUserId,
  exclusive = true,
}: {
  name?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  extensions: PbxExtensionOption[];
  required?: boolean;
  allowEmpty?: boolean;
  emptyLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  /** When set, extensions taken by other users are disabled (1 desk = 1 user). */
  currentUserId?: string;
  exclusive?: boolean;
}) {
  const selected = value ?? defaultValue;
  const numbers = [
    ...new Set([...extensions.map((e) => e.number).filter(Boolean), selected].filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const bound = onChange
    ? {
        value: selected,
        onChange: (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => onChange(e.target.value),
      }
    : { defaultValue: selected };

  if (loading) {
    return (
      <select name={name} disabled value="">
        <option value="">Loading IP-PBX extensions…</option>
      </select>
    );
  }

  if (!extensions.length) {
    return (
      <input
        name={name}
        required={required}
        disabled={disabled}
        placeholder="Must exist on the PBX"
        {...bound}
      />
    );
  }

  return (
    <select name={name} required={required} disabled={disabled} {...bound}>
      {(allowEmpty || !selected) && <option value="">{emptyLabel}</option>}
      {numbers.map((n) => {
        const row = extensions.find((e) => e.number === n);
        const takenByOther =
          exclusive &&
          Boolean(
            row?.mapped?.some(
              (m) => m?.name && (!currentUserId || String(m.id) !== String(currentUserId)),
            ),
          );
        const isSelected = String(n) === String(selected);
        return (
          <option key={n} value={n} disabled={takenByOther && !isSelected}>
            {optionLabel(n, row, currentUserId)}
          </option>
        );
      })}
    </select>
  );
}
